<template>
	<v-card class="category-card elevation-0 d-flex flex-column pt-2">
		<div class="category-card__container">
			<div class="category-card__outer">
				<div class="d-flex category-card__list">
					<ProfileBadge
						v-if="category.pin_to_top || category.sponsored_by"
						isPin
						:pin="pinnedType(category)"
					/>

					<div
						class="d-flex justify-space-between px-6 py-5"
						v-bind:style="{
							marginTop: category.pin_to_top || category.sponsored_by ? '20px' : 0,
						}"
					>
						<router-link
							:to="{
								name: 'forums-by-category-id',
								params: { id: category.category_id },
							}"
							class="d-flex category-card__title"
						>
							<div class="category-card__meta mr-2">{{ category.category_name }}</div>
						</router-link>

						<div class="d-flex">
							<div class="d-flex mr-6">
								<Icon
									type="eye"
									color="var(--accent-color)"
									:size="24"
									:pr="8"
								/>
								<div class="category-card__counter-num mr-2">
									{{ category.forum_count }}
								</div>
							</div>
							<div class="d-flex">
								<Icon
									type="comment"
									color="var(--accent-color)"
									:size="24"
									:pr="8"
								/>
								<div class="category-card__counter-num mr-2">
									{{ category.forum_reply_count }}
								</div>
							</div>
						</div>
					</div>

					<v-divider v-if="category.lastActiveForum" />

					<router-link
						:to="{
							name: 'forums-by-category-id',
							params: { id: category.category_id },
						}"
						class="d-flex flex-column category-card__title"
					>
						<div class="d-flex px-6 pt-6 category-card__title" v-html="lastActiveForum.forum_text">
						</div>

						<router-link
							:to="{
								name: isCurrentUser(this.user.user_id) ? 'profile' : 'user-by-id',
								params: isCurrentUser(this.user.user_id)
									? []
									: { id: this.user.user_id },
							}"
							class="d-flex align-center justify-space-between text-decoration-none"
							:class="{ disabled: this.user.user_hidden }"
						>
							<div
								class="d-flex align-center px-6 py-6"
								v-if="category.lastActiveForum"
							>
								<Avatar
									class="mr-3 category-card__avatar"
									:size="50"
									:avatar="user.user_logo"
								/>
								<div class="category-card__author">
									{{ user.visible_username }}
								</div>
							</div>
							<div class="category-card__date" v-if="lastActiveForum.created">
								{{ calcDateAgo(lastActiveForum.created) }}
							</div>
						</router-link>
					</router-link>

					<v-divider />

					<div class="d-flex justify-end px-6 py-6">
						<div
							class="category-card__follow"
							:class="isFollowed && 'category-card__follow-active'"
							style="cursor: pointer"
							@click="onFollow"
						>
							{{ isFollowed ? 'Joined' : 'Join' }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { threadCategoryToggleFollow, threadCategoryToggleUnFollow } from '@/api/forum'
import Avatar from '@/components/Avatar'
import { mapGetters } from 'vuex'
import pinnedIcon from '@/assets/images/icon-pinned.svg'
import sponsoredStarIcon from '@/assets/images/icon-sponsored-star.svg'
import ProfileBadge from '@/components/ProfileBadge.vue'
import moment from 'moment'

Vue.use(VueScrollTo)

export default {
	name: 'CategoryCard',

	components: {
		Avatar,
		ProfileBadge
	},

	props: {
		category: {
			type: Object,
			default: () => ({}),
		},
	},

	data() {
		return {
			isFollowed: false,
			pinnedIcon,
			sponsoredStarIcon,
		}
	},

	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated']),

		user() {
			if (this.category.lastActiveForum) {
				return this.category.lastActiveForum.user
			}

			return {}
		},

		lastActiveForum() {
			return this.category.lastActiveForum || {}
		},
	},

	methods: {
		onFollow() {
			if (this.isFollowed) {
				threadCategoryToggleUnFollow(this.category.category_id)
			} else {
				threadCategoryToggleFollow({ thread_category_id: this.category.category_id })
			}

			this.isFollowed = !this.isFollowed
		},
		isCurrentUser(userId) {
			if (!this.profile) {
				return false
			}

			const { user_id: currentUserId } = this.profile
			return userId === currentUserId
		},
		pinnedType(forum) {
			let result = {}

			if (forum.pin_to_top && forum.sponsored_by) {
				return (result = {
					icon: sponsoredStarIcon,
					text: 'Sponsored by ' + forum.sponsored_by,
				})
			} else if (forum.sponsored_by) {
				return (result = {
					icon: sponsoredStarIcon,
					text: 'Sponsored by ' + forum.sponsored_by,
				})
			} else if (forum.pin_to_top) {
				result = { icon: pinnedIcon, text: 'Pinned' }
			} else {
				result = { icon: pinnedIcon, text: 'Pinned' }
			}

			return result
		},
		calcDateAgo(date) {
			return moment(date * 1000).fromNow()
		},
	},

	created() {
		if (this.category) {
			this.isFollowed = this.category.follow
		}
	},
}
</script>

<style lang="scss">
.category-card {
	background: #f1f1f1 !important;

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.06);
		background: white !important;
	}

	&__counter {
		margin-right: 24px;
		@media (max-width: 480px) {
			margin: 0;
		}

		&-wrapper {
			display: flex;
			@media (max-width: 480px) {
				flex-direction: column;
				align-items: flex-end;
			}
		}

		&-num {
			color: #697583;
			font-size: 15px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-align: right;
			display: flex;
			align-items: center;
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: var(--username-category-title);
		letter-spacing: 0.36px;
		gap: 4px;
		word-break: break-word;
	}

	&__date {
		font-size: 13px;
		color: #697583;
		line-height: 19.37px;
		margin-right: 16px;
		text-align: right;
	}

	&__date {
		font-size: 13px;
		color: #697583;
		line-height: 19.37px;
		margin-right: 16px;
		text-align: right;
	}

	&__author {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: var(--username-category-title);
		letter-spacing: 0.36px;
		word-break: break-word;
	}

	&__follow {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-decoration: underline;

		&-active {
			color: rgb(160, 160, 160);
		}
	}

	&__avatar {
		img {
			object-fit: cover;
		}
	}

	&__outer {
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: #4a4a4a !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: 400 !important;
		letter-spacing: 0;
		line-height: 19.5px;
		font-style: normal;
		text-decoration: none;
		word-break: break-word;

		a {
			color: green !important;
			text-decoration: none;
		}
	}

	@media screen and (max-width: 1904px) {
		&__list {
			flex: 0 0 75%;
		}
	}
	@media screen and (max-width: 1264px) {
		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}
	@media screen and (max-width: 768px) {
		&__outer {
			flex-direction: column;
		}
	}
}
</style>
